import Breadcrumbs from "@/components/Breadcrumbs";
import ConfirmDeleteDialog from "@/components/ConfirmDeleteDialog";
import { SettingsService } from "@/services/admin-services";
import { SettingsFooterLink } from "@/services/admin-services/models";
import { LocaleService } from "@/services/global-services";
import { Language, Locale } from "@/services/global-services/models";
import { PlusIcon } from "@heroicons/react/24/outline";
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import LinkDialog from "./components/LinkDialog";

const breadcrumbs = [
    {
        to: "../../..",
        title: "Administration",
    },
    {
        title: "Settings",
        to: "../../..",
    },
    {
        title: "Footer Links",
        to: "..",
    },
];

const Details = () => {
    const [breadcrumbTrail, setBreadcrumbTrail] = useState(breadcrumbs);
    const [data, setData] = useState<Array<SettingsFooterLink>>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [languages, setLanguages] = useState<Array<Language>>([]);

    const [dialogLink, setDialogLink] = useState<SettingsFooterLink | null>();
    const [removeLink, setRemoveLink] = useState<string | null>(null);

    const settingsService = useMemo(() => new SettingsService("int"), []);

    const { identifier } = useParams();

    useEffect(() => {
        Promise.all([
            settingsService.getFooterLinks().then((data) => {
                setData(data.filter((x) => x.language === identifier));
            }),
            LocaleService.get().then(parseLanguages),
        ]).then(() => setIsLoading(false));
    }, [settingsService, identifier]);

    useEffect(() => {
        if (!data) return;

        const language = languages.find((x) => x.slug === identifier);
        if (!language) return;

        setBreadcrumbTrail([...breadcrumbs, { title: `${language.label}`, to: "" }]);
    }, [data, languages, identifier]);

    const parseLanguages = (regions: Array<Locale>) => {
        const languages: Array<Language> = [];
        for (let i = 0; i < regions.length; i++) {
            const region = regions[i];

            for (let j = 0; j < region.languages.length; j++) {
                const language = region.languages[j];
                if (languages.findIndex((x) => x.label === language.label) > -1) continue;

                const split = language.slug.split("-");
                languages.push({
                    ...language,
                    slug: `int-${split[1]}`,
                    regionLabel: "International",
                });
            }
        }

        setLanguages(languages.sort((a, b) => a.label.localeCompare(b.label)));
    };

    const onCreate = () => {
        setDialogLink({
            id: "",
            language: identifier!,
            order: 0,
            targetUrl: "",
            linkText: "",
        });
    };

    const onEdit = (link: SettingsFooterLink) => {
        setDialogLink(link);
    };

    const onSubmitDialog = (link: SettingsFooterLink) => {
        setDialogLink(null);
        setData([...data.filter((x) => x.id !== link.id), link]);
    };

    const onDelete = (id: string) => setRemoveLink(id);

    const onCancelDelete = () => setRemoveLink(null);

    const onPerformDelete = () => {
        settingsService.removeFooterLink(removeLink!).then((success) => {
            if (success) {
                const list = data.filter((x) => x.id !== removeLink);
                setData(list);
            }

            setRemoveLink(null);
        });
    };

    if (isLoading || !data)
        return (
            <Box>
                <Breadcrumbs trail={breadcrumbTrail} />

                <Divider />
                <Box m={4} sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 500 }}>
                    <CircularProgress />
                </Box>
            </Box>
        );

    return (
        <Box>
            <Breadcrumbs trail={breadcrumbTrail} />

            <Divider />

            <Grid container spacing={2} p={{ xs: 2, md: 4 }}>
                <Grid size={{ xs: 12, md: 12 }} sx={{ display: "flex" }}>
                    <Button
                        variant="contained"
                        startIcon={<PlusIcon className="h-5 w-5" />}
                        sx={{ ml: "auto", mb: { xs: 0, md: 2 } }}
                        onClick={() => onCreate()}
                    >
                        Add Link
                    </Button>
                </Grid>

                <Grid size={{ xs: 12, md: 12 }}>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 750 }} aria-label="simple table">
                            <TableHead sx={{ bgcolor: "alternate.main" }}>
                                <TableRow>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Text
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Target
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ width: 100 }}>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Order
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ width: 200 }} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={3}
                                            sx={{
                                                height: 400,
                                                alignItems: "center",
                                                flexDirection: "column",
                                            }}
                                        >
                                            <Box sx={{ display: "flex", placeContent: "center" }}>
                                                <CircularProgress />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : data.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={4}
                                            sx={{
                                                height: 400,
                                                alignItems: "center",
                                                flexDirection: "column",
                                            }}
                                        >
                                            <Box sx={{ display: "flex", placeContent: "center" }}>
                                                <Typography variant="body2">No links available.</Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    data
                                        .sort((a, b) => a.order - b.order)
                                        .map((item, i) => (
                                            <TableRow
                                                key={i}
                                                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                                            >
                                                <TableCell component="th" scope="row">
                                                    <Typography color={"text.secondary"} variant={"subtitle2"}>
                                                        {item.linkText}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell scope="row">
                                                    <Typography color={"text.secondary"} variant={"subtitle2"}>
                                                        {item.targetUrl}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell scope="row">
                                                    <Typography color={"text.secondary"} variant={"subtitle2"}>
                                                        {item.order}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell scope="row" sx={{ p: 0 }}>
                                                    <Box sx={{ display: "flex" }}>
                                                        <Button
                                                            color={"primary"}
                                                            sx={{ ml: "auto", mr: 1 }}
                                                            onClick={() => onEdit(item)}
                                                        >
                                                            Edit
                                                        </Button>
                                                        <Button
                                                            color={"error"}
                                                            sx={{ mr: 1 }}
                                                            onClick={() => onDelete(item.id)}
                                                        >
                                                            Delete
                                                        </Button>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>

            {dialogLink && (
                <LinkDialog link={dialogLink} onClose={() => setDialogLink(undefined)} onSubmit={onSubmitDialog} />
            )}

            {removeLink && (
                <ConfirmDeleteDialog
                    open={Boolean(removeLink)}
                    title={"Delete link"}
                    message={"Are you sure you wish to delete this link? You can not undo this action!"}
                    onCancel={onCancelDelete}
                    onDelete={onPerformDelete}
                />
            )}
        </Box>
    );
};

export default Details;
